import React from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import WhyChooseUs from '../components/WhyChooseUs';
import ServiceCard from '../components/ServiceCard';
import PricingCard from '../components/PricingCard';
import { servicesData } from '../data/services';
import { pricingData } from '../data/pricing';
import { ArrowRight, Activity, Heart, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  return (
    <div className='bg-[#050505] text-gray-300'>
      {' '}
      {/* Changed from bg-white */}
      {/* 1. Hero Section - Ensure Hero component handles its own dark overlay */}
      <Hero lang={lang} />
      {/* 2. Trust Bar / Stats - Darkened for seamless transition */}
      <section className='py-12 bg-medical-blue-950 border-y border-white/5'>
        <div className='section-padding max-w-7xl mx-auto flex flex-wrap justify-between gap-8'>
          {[
            {
              icon: <Activity className='text-medical-blue-400' />,
              textEn: '100% Licensed Staff',
              textAr: 'طاقم مرخص ١٠٠٪',
            },
            {
              icon: <Heart className='text-red-500' />, // Brighter red for dark bg
              textEn: 'Compassionate Care',
              textAr: 'رعاية رحيمة',
            },
            {
              icon: <Award className='text-trust-teal' />,
              textEn: 'Premium Quality',
              textAr: 'جودة ممتازة',
            },
          ].map((item, i) => (
            <div key={i} className='flex items-center gap-4 text-white'>
              <div className='p-3 bg-white/5 border border-white/10 rounded-xl'>
                {item.icon}
              </div>
              <span className='font-bold text-sm tracking-wide'>
                {isAr ? item.textAr : item.textEn}
              </span>
            </div>
          ))}
        </div>
      </section>
      {/* 3. Services Overview - Deep Black bg */}
      <section
        className='py-24 section-padding bg-[#080808]'
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className='max-w-7xl mx-auto'>
          <div className='flex flex-col md:flex-row justify-between items-end mb-16 gap-6'>
            <div className={isAr ? 'text-right' : 'text-left'}>
              <h2 className='text-4xl md:text-5xl font-black text-white mb-4'>
                {' '}
                {/* Changed to text-white */}
                {isAr ? 'خدماتنا المتميزة' : 'Our Specialized Services'}
              </h2>
              <p className='text-lg text-gray-400 font-medium max-w-xl'>
                {' '}
                {/* Changed to text-gray-400 */}
                {isAr
                  ? 'رعاية طبية منزلية مصممة خصيصاً لتناسب احتياجاتكم الصحية واليومية.'
                  : 'Tailored home medical care designed to fit your health and daily living needs.'}
              </p>
            </div>
            <Link
              to='/services'
              className='group flex items-center gap-2 text-medical-blue-400 font-black uppercase tracking-widest text-sm hover:text-white transition-colors'
            >
              {isAr ? 'عرض كل الخدمات' : 'View All Services'}
              <ArrowRight
                className={`transition-transform ${isAr ? 'rotate-180 group-hover:-translate-x-2' : 'group-hover:translate-x-2'}`}
              />
            </Link>
          </div>

          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {servicesData.map((service, index) => (
              <ServiceCard
                key={service.id}
                lang={lang}
                title={service.title[lang]}
                description={service.description[lang]}
                features={service.features[lang].slice(0, 4)}
                image={service.image}
                delay={index * 0.1}
                isDark={true} // Ensure ServiceCard component supports a dark mode prop
              />
            ))}
          </div>
        </div>
      </section>
      {/* 4. Why Choose Us Section - Ensure this component uses dark glass cards */}
      <WhyChooseUs lang={lang} />
      {/* 5. Pricing Highlights - Pure Black bg */}
      <section
        className='py-24 section-padding bg-black'
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className='max-w-7xl mx-auto text-center'>
          <h2 className='text-4xl md:text-5xl font-black text-white mb-16'>
            {' '}
            {/* Changed to text-white */}
            {isAr ? 'باقات الرعاية الأكثر طلباً' : 'Most Popular Care Packages'}
          </h2>

          <div className='grid md:grid-cols-2 gap-12 max-w-4xl mx-auto'>
            <PricingCard
              lang={lang}
              title={isAr ? 'دوام ١٢ ساعة (أسبوعي)' : '12h Duty (Weekly)'}
              price={pricingData.shift12[1].price}
              duration={pricingData.shift12[1].duration[lang]}
              features={pricingData.shift12[1].features[lang]}
              isPopular={true}
              delay={0.1}
            />
            <PricingCard
              lang={lang}
              title={isAr ? 'دوام ٢٤ ساعة (أسبوعي)' : '24h Duty (Weekly)'}
              price={pricingData.shift24[1].price}
              duration={pricingData.shift24[1].duration[lang]}
              features={pricingData.shift24[1].features[lang]}
              isPopular={false}
              delay={0.2}
            />
          </div>

          <div className='mt-16'>
            <Link
              to='/pricing'
              className='px-10 py-4 border-2 border-white/10 rounded-full font-black text-white hover:bg-medical-blue-600 hover:border-medical-blue-600 transition-all'
            >
              {isAr ? 'عرض جميع خطط الأسعار' : 'View Full Pricing Plans'}
            </Link>
          </div>
        </div>
      </section>
      {/* 6. Contact CTA Section - Dark with Glow Effect */}
      <section className='py-24 section-padding bg-[#050505] overflow-hidden relative'>
        {/* Background Glow */}
        <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-medical-blue-600/10 blur-[120px] rounded-full pointer-events-none' />

        <div className='max-w-7xl mx-auto flex flex-col items-center text-center relative z-10'>
          <div className='bg-white/5 border border-white/10 p-6 rounded-full text-medical-blue-400 mb-8'>
            <Activity size={48} className='animate-pulse' />
          </div>
          <h2 className='text-4xl md:text-6xl font-black text-white mb-8 max-w-4xl'>
            {isAr
              ? 'هل أنت مستعد لبدء الرعاية المهنية؟'
              : 'Ready to Start Professional Home Care?'}
          </h2>
          <p className='text-xl text-gray-400 mb-12 max-w-2xl font-medium'>
            {isAr
              ? 'تحدث إلينا اليوم للحصول على استشارة مجانية وبدء خطة رعاية مخصصة لعائلتك.'
              : 'Talk to us today for a free consultation and start a customized care plan for your family.'}
          </p>
          <div className='flex flex-wrap justify-center gap-6'>
            <Link
              to='/contact'
              className='px-12 py-5 bg-medical-blue-600 text-white rounded-2xl font-black text-xl shadow-2xl shadow-medical-blue-600/20 hover:bg-medical-blue-700 transition-all'
            >
              {isAr ? 'احجز موعداً' : 'Book Appointment'}
            </Link>
            <a
              href='https://wa.me/96550097296'
              className='px-12 py-5 bg-[#25D366] text-white rounded-2xl font-black text-xl shadow-2xl shadow-green-600/20 hover:bg-green-600 transition-all'
            >
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
